/**
 * MO.STORE — interaction layer (final, vanilla, no dependencies)
 * ---------------------------------------------------------------------------
 *  1. Reveal     [data-mo-reveal], [data-mo-split]  → .is-inview (one IO)
 *  2. Scrub      [data-mo-scrub]  → --mo-progress 0…1 while on screen
 *  3. Light      [data-mo-light]  → pointer-following light (desktop)
 *  4. Tilt       [data-mo-tilt]   → 3D tilt + border light (desktop)
 *  5. Ticker     [data-mo-ticker] → speeds up briefly while scrolling
 *  6. Summary    [data-mo-summary]→ live total when the quantity changes
 *  7. Rail       [data-mo-rail]   → prev / next arrows for "Les autres packs"
 *
 * Writes CSS custom properties / classes only; all styling lives in
 * mo-store-custom.css. One scroll listener, rAF-throttled. Pointer effects
 * only run on fine pointers. Everything respects prefers-reduced-motion and
 * re-initialises after section reloads in the theme editor.
 */
(() => {
  const root = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const motionOK = () => !reduceMotion.matches;
  const pointerFX = () => finePointer.matches && motionOK();
  const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

  /* ------------------------------------------------------------ 1. reveal */

  function splitWords(el) {
    if (el.dataset.moSplitDone) return;
    const text = el.textContent.trim().replace(/\s+/g, ' ');
    if (!text) return;
    el.setAttribute('aria-label', text);
    el.textContent = '';
    text.split(' ').forEach((word, i, all) => {
      const outer = document.createElement('span');
      outer.className = 'mo-split-word';
      outer.setAttribute('aria-hidden', 'true');
      const inner = document.createElement('span');
      inner.style.setProperty('--w', String(i));
      inner.textContent = word;
      outer.appendChild(inner);
      el.appendChild(outer);
      if (i < all.length - 1) el.appendChild(document.createTextNode(' '));
    });
    el.dataset.moSplitDone = 'true';
  }

  let revealObserver = null;

  function initReveal(scope = document) {
    const items = scope.querySelectorAll('[data-mo-reveal]:not(.is-inview), [data-mo-split]:not(.is-inview)');
    scope.querySelectorAll('[data-mo-split]').forEach(splitWords);

    if (!motionOK() || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-inview'));
      root.classList.add('mo-reveal-ready');
      return;
    }

    revealObserver ??= new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-inview');
          revealObserver.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );

    root.classList.add('mo-reveal-ready');
    // Let the hidden state paint once, then observe (on-screen items animate in).
    requestAnimationFrame(() => {
      requestAnimationFrame(() => items.forEach((el) => revealObserver.observe(el)));
    });
  }

  /* ------------------------------------------------------------- 2. scrub */

  const scrubbed = new Set();
  let scrubObserver = null;

  function initScrub(scope = document) {
    if (!('IntersectionObserver' in window)) return;
    scrubObserver ??= new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => (e.isIntersecting ? scrubbed.add(e.target) : scrubbed.delete(e.target)));
        requestTick();
      },
      { rootMargin: '10% 0px 10% 0px' }
    );
    scope.querySelectorAll('[data-mo-scrub]').forEach((el) => scrubObserver.observe(el));
  }

  function updateScrub() {
    if (!motionOK()) return;
    const vh = window.innerHeight;
    scrubbed.forEach((el) => {
      const r = el.getBoundingClientRect();
      const p = clamp((vh - r.top) / (vh + r.height), 0, 1);
      el.style.setProperty('--mo-progress', p.toFixed(4));
    });
  }

  /* ------------------------------------------------- 5. ticker scroll boost */

  let lastY = window.scrollY;
  let lastT = performance.now();
  let boost = 0;
  let boostFrame = 0;

  function tickerAnimations() {
    return [...document.querySelectorAll('[data-mo-ticker] .mo-ticker__track')]
      .flatMap((track) => (track.getAnimations ? track.getAnimations() : []));
  }

  function decayBoost() {
    boost *= 0.92;
    const rate = 1 + boost;
    tickerAnimations().forEach((a) => (a.updatePlaybackRate ? a.updatePlaybackRate(rate) : (a.playbackRate = rate)));
    boostFrame = boost > 0.02 ? requestAnimationFrame(decayBoost) : 0;
  }

  function measureVelocity() {
    const now = performance.now();
    const dy = Math.abs(window.scrollY - lastY);
    const dt = Math.max(now - lastT, 1);
    lastY = window.scrollY;
    lastT = now;
    if (!motionOK()) return;
    boost = clamp(Math.max(boost, (dy / dt) * 2.2), 0, 4);
    if (!boostFrame) boostFrame = requestAnimationFrame(decayBoost);
  }

  /* ------------------------------------------------------ shared scroll tick */

  let ticking = false;
  function requestTick() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      ticking = false;
      updateScrub();
    });
  }

  window.addEventListener(
    'scroll',
    () => {
      requestTick();
      measureVelocity();
      tiltRect = null;
    },
    { passive: true }
  );
  window.addEventListener('resize', requestTick, { passive: true });

  /* ------------------------------------------------------------- 3. light */

  let lit = null;
  let litRect = null;
  let target = { x: 0, y: 0 };
  let current = { x: 0, y: 0 };
  let lightFrame = 0;

  function lightLoop() {
    current.x += (target.x - current.x) * 0.12;
    current.y += (target.y - current.y) * 0.12;
    if (lit) {
      lit.style.setProperty('--mo-cx', `${current.x.toFixed(1)}px`);
      lit.style.setProperty('--mo-cy', `${current.y.toFixed(1)}px`);
    }
    const moving = Math.abs(target.x - current.x) + Math.abs(target.y - current.y) > 0.5;
    lightFrame = lit && moving ? requestAnimationFrame(lightLoop) : 0;
  }

  /* -------------------------------------------------------------- 4. tilt */

  const MAX_TILT = 3;   // degrees — kept subtle: the product stays the hero
  const MAX_SHIFT = 4;
  const MAX_SHADOW = 10;
  let tilted = null;
  let tiltRect = null;
  let tiltFrame = 0;
  let lastPointer = null;

  function applyTilt() {
    tiltFrame = 0;
    if (!tilted || !lastPointer) return;
    tiltRect ??= tilted.getBoundingClientRect();
    const x = clamp((lastPointer.clientX - tiltRect.left) / tiltRect.width, 0, 1);
    const y = clamp((lastPointer.clientY - tiltRect.top) / tiltRect.height, 0, 1);
    const dx = x - 0.5;
    const dy = y - 0.5;
    const s = tilted.style;
    s.setProperty('--mo-rx', `${(-dy * MAX_TILT).toFixed(2)}deg`);
    s.setProperty('--mo-ry', `${(dx * MAX_TILT).toFixed(2)}deg`);
    s.setProperty('--mo-px', `${(x * 100).toFixed(1)}%`);
    s.setProperty('--mo-py', `${(y * 100).toFixed(1)}%`);
    s.setProperty('--mo-tx', `${(-dx * MAX_SHIFT).toFixed(2)}px`);
    s.setProperty('--mo-ty', `${(-dy * MAX_SHIFT).toFixed(2)}px`);
    tilted.parentElement?.style.setProperty('--mo-sx', `${(-dx * MAX_SHADOW).toFixed(2)}px`);
  }

  function releaseTilt(card) {
    card.classList.remove('is-tilting');
    ['--mo-rx', '--mo-ry', '--mo-px', '--mo-py', '--mo-tx', '--mo-ty'].forEach((p) => card.style.removeProperty(p));
    card.parentElement?.style.removeProperty('--mo-sx');
  }

  /* one delegated pointer listener drives light + tilt */
  document.addEventListener(
    'pointermove',
    (event) => {
      if (event.pointerType !== 'mouse' || !pointerFX()) return;
      const el = event.target instanceof Element ? event.target : null;

      // light
      const stage = el?.closest('[data-mo-light]') ?? null;
      if (stage !== lit) {
        lit?.classList.remove('is-lit');
        lit = stage;
        litRect = null;
        if (lit) {
          lit.classList.add('is-lit');
          litRect = lit.getBoundingClientRect();
          current = { x: event.clientX - litRect.left, y: event.clientY - litRect.top };
        }
      }
      if (lit) {
        litRect = lit.getBoundingClientRect();
        target = { x: event.clientX - litRect.left, y: event.clientY - litRect.top };
        if (!lightFrame) lightFrame = requestAnimationFrame(lightLoop);
      }

      // tilt
      const card = el?.closest('[data-mo-tilt]') ?? null;
      if (card !== tilted) {
        if (tilted) releaseTilt(tilted);
        tilted = card;
        tiltRect = null;
        tilted?.classList.add('is-tilting');
      }
      if (tilted) {
        lastPointer = event;
        if (!tiltFrame) tiltFrame = requestAnimationFrame(applyTilt);
      }
    },
    { passive: true }
  );

  root.addEventListener('pointerleave', () => {
    if (tilted) releaseTilt(tilted);
    tilted = null;
    lit?.classList.remove('is-lit');
    lit = null;
  });

  /* ----------------------------------------------------------- 6. summary */

  function formatMoney(cents, format) {
    const fmt = (format || '{{amount}}').replace(/<[^>]*>/g, '');
    const match = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    const value = Number(cents) / 100;
    const group = (num, decimals, thousands, decimal) => {
      const [int, frac] = num.toFixed(decimals).split('.');
      return int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? decimal + frac : '');
    };
    const styles = {
      amount: [2, ',', '.'],
      amount_no_decimals: [0, ',', '.'],
      amount_with_comma_separator: [2, '.', ','],
      amount_no_decimals_with_comma_separator: [0, '.', ','],
      amount_with_space_separator: [2, ' ', ','],
      amount_no_decimals_with_space_separator: [0, ' ', ','],
      amount_with_apostrophe_separator: [2, "'", '.'],
    };
    const [d, t, s] = styles[match?.[1]] ?? styles.amount;
    const amount = group(value, d, t, s);
    return match ? fmt.replace(match[0], amount) : amount;
  }

  function updateSummary(summary) {
    const section = summary.closest('.shopify-section') ?? document;
    const qtyInput = section.querySelector('form[data-type="add-to-cart-form"] input[name="quantity"], input[name="quantity"]');
    const qty = Math.max(parseInt(qtyInput?.value, 10) || 1, 1);
    const price = Number(summary.dataset.price || 0);
    const saving = Number(summary.dataset.saving || 0);
    const format = summary.dataset.moneyFormat;

    const qtyEl = summary.querySelector('[data-mo-summary-qty]');
    const totalEl = summary.querySelector('[data-mo-summary-total]');
    const savingEl = summary.querySelector('[data-mo-summary-saving]');
    const total = formatMoney(price * qty, format);

    if (qtyEl) qtyEl.textContent = String(qty);
    if (savingEl) savingEl.textContent = formatMoney(saving * qty, format);
    if (totalEl && totalEl.textContent !== total) {
      totalEl.textContent = total;
      if (motionOK()) {
        totalEl.classList.remove('is-bumping');
        void totalEl.offsetWidth;
        totalEl.classList.add('is-bumping');
      }
    }
  }

  const refreshSummaries = () => document.querySelectorAll('[data-mo-summary]').forEach(updateSummary);
  const refreshSoon = () => {
    refreshSummaries();
    setTimeout(refreshSummaries, 120);
    setTimeout(refreshSummaries, 600);
  };

  document.addEventListener('input', (e) => e.target?.name === 'quantity' && refreshSummaries());
  document.addEventListener('change', (e) => (e.target?.name === 'quantity' ? refreshSummaries() : refreshSoon()));
  document.addEventListener('quantity-selector:update', refreshSoon);
  document.addEventListener('shopify:product:select', refreshSoon);
  document.addEventListener('click', (e) => {
    if (e.target instanceof Element && e.target.closest('.quantity-selector, quantity-selector-component')) refreshSoon();
  });

  // Horizon re-renders the product section when the size changes: watch for it.
  function watchSummaries() {
    document.querySelectorAll('[data-mo-summary]').forEach((summary) => {
      if (summary.dataset.moWatched) return;
      summary.dataset.moWatched = 'true';
      new MutationObserver(refreshSummaries).observe(summary, { attributes: true, attributeFilter: ['data-price'] });
    });
  }


  /* -------------------------------------------------------------- 7. rail */

  function initRails(scope = document) {
    scope.querySelectorAll('[data-mo-rail]').forEach((rail) => {
      if (rail.dataset.moRailReady) return;
      rail.dataset.moRailReady = 'true';
      const section = rail.closest('.mo-more') ?? rail.parentElement;
      const prev = section?.querySelector('[data-mo-rail-prev]');
      const next = section?.querySelector('[data-mo-rail-next]');
      if (!prev || !next) return;

      const step = () => {
        const item = rail.querySelector('.mo-rail__item');
        const gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
        const itemW = item ? item.getBoundingClientRect().width + gap : rail.clientWidth * 0.8;
        return Math.max(itemW, Math.floor(rail.clientWidth / itemW) * itemW);
      };
      const sync = () => {
        const max = rail.scrollWidth - rail.clientWidth - 2;
        prev.disabled = rail.scrollLeft <= 2;
        next.disabled = rail.scrollLeft >= max;
        section.querySelector('[data-mo-rail-nav]')?.toggleAttribute('hidden', max <= 0);
      };
      const go = (dir) => rail.scrollBy({ left: dir * step(), behavior: motionOK() ? 'smooth' : 'auto' });

      prev.addEventListener('click', () => go(-1));
      next.addEventListener('click', () => go(1));
      rail.addEventListener('scroll', () => requestAnimationFrame(sync), { passive: true });
      window.addEventListener('resize', sync, { passive: true });
      sync();
    });
  }

  /* -------------------------------------------------------------- init */

  function init(scope = document) {
    initReveal(scope);
    initScrub(scope);
    initRails(scope);
    watchSummaries();
    refreshSummaries();
    requestTick();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  reduceMotion.addEventListener?.('change', () => {
    if (tilted) releaseTilt(tilted);
    tilted = null;
    initReveal();
  });
})();
